import React, {FC, useEffect, useState} from "react";
import {QuestionProps} from "../../../pages/form";
import {Col, Input, Row, Skeleton} from "antd";
import {useDebouncedCallback} from "use-debounce";
import {useRouter} from "next/router";
import {useIntl} from "react-intl";
import {QuestionTag} from "./components/question-tag";
import {extractTranslation} from "../../../helpers/formatters";
import {
  Form_FormOptionMsg,
  useListFormOptionsLazyQuery,
  useListFormOptionsQuery,
  useSearchFormOptionsLazyQuery
} from "../../../src/generated/graphql";

interface SelectQuestionProps extends QuestionProps {
  clinicId: string;
}

// TODO implement search
export const SelectQuestion: FC<SelectQuestionProps> = ({
  answer,
  onAnswer,
  question,
  clinicId,
}) => {
  const { formatMessage } = useIntl();
  const locale = useRouter().locale;

  const selectQuestion = question?.select;

  const datasetId = selectQuestion?.datasetId;
  const searchable = selectQuestion?.searchable;

  const [searchTerm, setSearchTerm] = useState<string>("");

  const [internalOptionId, setInternalOptionId] = useState<string | undefined>(undefined);
  useEffect(() => {
    setInternalOptionId(currentInternalOptionId => {
      if (answer?.value?.select === undefined || answer?.value?.select === null) {
        return currentInternalOptionId;
      }

      return answer.value.select.option?.id ?? undefined;
    })
  }, [answer])


  const { data: listFormOptionsData, loading: listFormOptionsLoading } =
      useListFormOptionsQuery({
        skip: searchable !== false || !datasetId,
        variables: {
          clinicId,
          datasetIds: [datasetId!],
        }
      });

  const [searchFormOptionsQuery, { data: searchFormOptionsData, loading: searchFormOptionsLoading }] =
      useSearchFormOptionsLazyQuery();

  const [options, setOptions] = useState<Form_FormOptionMsg[]>([]);
  useEffect(() => {
    setOptions(currentOptions => {
      if (searchable === true) {
        if (!!searchFormOptionsData) {
          return searchFormOptionsData?.searchFormOptions?.response?.optionGroups?.flatMap(optionGroup => optionGroup?.options).map(option => option!) ?? []
        }
      } else {
        if (!!listFormOptionsData) {
          return listFormOptionsData?.listFormOptions?.response?.options?.map(option => option!) ?? []
        }
      }
      return currentOptions;
    })
  }, [searchable, listFormOptionsData, searchFormOptionsData])

  const searchDebounced = useDebouncedCallback((query: string) => {
    if (!datasetId || searchable !== true) {
      return;
    }

    searchFormOptionsQuery({
      variables: {
        clinicId,
        formDatasetId: datasetId,
        query: query
      }
    })
  }, 400);

  useEffect(() => {
    searchDebounced(searchTerm);
  }, [searchDebounced, searchTerm]);

  function onOptionClick(clickedOptionId: string) {
    let newInternalOptionId: string | undefined;
    if (clickedOptionId === internalOptionId) {
      newInternalOptionId = undefined;
    } else {
      newInternalOptionId = clickedOptionId;
    }

    setInternalOptionId(newInternalOptionId);
    onAnswer({
      select: {
        option: {id:newInternalOptionId},
      },
    });
  }

  return (
    <div>
      {searchable && (
        <Row>
          <Col span={24}>
            <Input
              placeholder={formatMessage({ id: "placeholder.search" })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Col>
        </Row>
      )}
      <Skeleton loading={searchable ? searchFormOptionsLoading : listFormOptionsLoading}>
        <Row>
          <Col span={24}>
            {options?.map((option) => {
              const title = extractTranslation(option.title, locale) || "-";
              const subtitle = extractTranslation(option.subtitle, locale);

              const selected = internalOptionId === option.id;

              return (
                <QuestionTag
                  key={option?.id}
                  text={title}
                  selected={selected}
                  tooltip={subtitle}
                  onClick={() => {
                    onOptionClick(option!.id!);
                  }}
                />
              );
            })}
          </Col>
        </Row>
      </Skeleton>
    </div>
  );
};
